import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'sent' | 'error'

const inputClass = 'w-full rounded-xl border border-input bg-background/70 px-4 py-3 text-base text-foreground placeholder:text-muted-foreground transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25'

export const SuggestToolForm: FC = (): ReactNode => {
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [nameError, setNameError] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const form = event.currentTarget
        const data = new FormData(form)
        const toolName = String(data.get('toolName') ?? '').trim()

        if (!toolName) {
            setNameError('Please tell us the name of the tool.')
            form.querySelector<HTMLInputElement>('#suggest-name')?.focus()
            return
        }

        setNameError('')
        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/suggest-tool', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    toolName,
                    website: String(data.get('website') ?? ''),
                    note: String(data.get('note') ?? ''),
                    email: String(data.get('email') ?? ''),
                    company: String(data.get('company') ?? '')
                })
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, message?: string } | null

            if (!response.ok || !result?.ok) {
                setStatus('error')
                setMessage(result?.message || 'Something went wrong. Please try again.')
                return
            }

            form.reset()
            setStatus('sent')
        }
        catch {
            setStatus('error')
            setMessage('We could not reach the server. Check your connection and try again.')
        }
    }

    if (status === 'sent') {
        return (
            <div role='status' className='flex flex-col items-start gap-4 rounded-3xl border border-primary/30 bg-primary/5 p-8'>
                <CircleCheck className='size-8 text-primary' aria-hidden='true' />

                <div>
                    <h3 className='font-heading text-xl font-semibold text-foreground'>
                        Thanks, we got it.
                    </h3>
                    <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                        Your suggestion is in the review queue for the next ranking update.
                    </p>
                </div>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-primary'
                >
                    Suggest another tool
                </button>
            </div>
        )
    }

    return (
        <form
            onSubmit={handleSubmit}
            noValidate
            className='grid gap-4 rounded-3xl border border-border bg-card p-6 sm:grid-cols-2 sm:p-8'
        >
            <div className='sm:col-span-1'>
                <label htmlFor='suggest-name' className='text-sm font-medium text-foreground'>
                    Tool name
                </label>
                <input
                    id='suggest-name'
                    name='toolName'
                    type='text'
                    required
                    maxLength={120}
                    autoComplete='off'
                    placeholder='For example, Vidu'
                    aria-invalid={nameError ? true : undefined}
                    aria-describedby={nameError ? 'suggest-name-error' : undefined}
                    className={cn(inputClass, 'mt-2', nameError && 'border-destructive')}
                />
                {nameError && (
                    <p id='suggest-name-error' className='mt-1.5 text-sm text-destructive'>
                        {nameError}
                    </p>
                )}
            </div>

            <div className='sm:col-span-1'>
                <label htmlFor='suggest-website' className='text-sm font-medium text-foreground'>
                    Website <span className='font-normal text-muted-foreground'>(optional)</span>
                </label>
                <input
                    id='suggest-website'
                    name='website'
                    type='text'
                    inputMode='url'
                    maxLength={300}
                    autoComplete='off'
                    placeholder='example.com'
                    className={cn(inputClass, 'mt-2')}
                />
            </div>

            <div className='sm:col-span-2'>
                <label htmlFor='suggest-note' className='text-sm font-medium text-foreground'>
                    Why is it worth listing? <span className='font-normal text-muted-foreground'>(optional)</span>
                </label>
                <textarea
                    id='suggest-note'
                    name='note'
                    rows={4}
                    maxLength={1000}
                    placeholder='What it does well, who it is for, or what changed on an existing listing.'
                    className={cn(inputClass, 'mt-2 resize-y')}
                />
            </div>

            <div className='sm:col-span-2'>
                <label htmlFor='suggest-email' className='text-sm font-medium text-foreground'>
                    Your email <span className='font-normal text-muted-foreground'>(optional, only used if we have a question)</span>
                </label>
                <input
                    id='suggest-email'
                    name='email'
                    type='email'
                    maxLength={200}
                    autoComplete='email'
                    placeholder='you@example.com'
                    className={cn(inputClass, 'mt-2')}
                />
            </div>

            <div className='hidden' aria-hidden='true'>
                <label htmlFor='suggest-company'>Company</label>
                <input id='suggest-company' name='company' type='text' tabIndex={-1} autoComplete='off' />
            </div>

            <div className='flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-xs text-muted-foreground'>
                    Suggestions are reviewed by hand. Listing is never paid.
                </p>

                <button
                    type='submit'
                    disabled={status === 'sending'}
                    className='inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:translate-y-0 disabled:opacity-70'
                >
                    {status === 'sending' && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
                    {status === 'sending' ? 'Sending...' : 'Send suggestion'}
                </button>
            </div>

            {status === 'error' && (
                <p role='alert' className='rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground sm:col-span-2'>
                    {message}
                </p>
            )}
        </form>
    )
}
